import { Helmet } from 'react-helmet';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Alert, Modal, Button, Spinner } from 'react-bootstrap';
import UserList from '../../components/admin/UserRuList';
import UserEdit from '../../components/admin/UserEdit';
import { updateUser, deleteUser, getUsers_ru,getUsers } from './../../api';
import useAuth from '../../hooks/useAuth';

function AdminDashboard() {
  // eslint-disable-next-line
  const title = 'List of All Users';
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const auth = useAuth();
  const currentUser = auth.getSession();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);  // Start loading
    try {
     // const response = await getUsers_ru();
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      setAlert({ variant: 'danger', message: 'Failed to fetch users' });
    } finally {
      setIsLoading(false);  // End loading regardless of success/failure
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };


  const handleDeleteShow = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(selectedUser.id);
      fetchUsers();
      handleDeleteClose();
    } catch (error) {
      console.error('Error deleting project:', error);
      setAlert({ variant: 'danger', message: 'Failed to delete user' });
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    try {
      await updateUser(updatedUser.id, updatedUser);
      fetchUsers();
      setShowModal(false);
      setSelectedUser(null);
      setAlert({ variant: 'success', message: 'User updated successfully' });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create user';
      setAlert({ variant: 'danger', message: errorMessage });
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };


  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {

        switch (sortConfig.key) {
          case 'projects':
          case 'files':
            return sortConfig.direction === 'ascending'
              ? a[sortConfig.key] - b[sortConfig.key]
              : b[sortConfig.key] - a[sortConfig.key];
          case 'disk_space':
            // Convert sizes back to bytes for sorting
            const getBytes = (sizeStr) => {
              const units = { 'B': 1, 'KB': 1024, 'MB': 1024 ** 2, 'GB': 1024 ** 3, 'TB': 1024 ** 4 };
              const [size, unit] = sizeStr.split(' ');
              return parseFloat(size) * units[unit];
            };
            const aBytes = getBytes(a.disk_space);
            const bBytes = getBytes(b.disk_space);
            return sortConfig.direction === 'ascending'
              ? aBytes - bBytes
              : bBytes - aBytes;
          case 'vector_space':
            // Convert sizes back to bytes for sorting
            const getBytes1 = (sizeStr) => {
              const units = { 'B': 1, 'KB': 1024, 'MB': 1024 ** 2, 'GB': 1024 ** 3, 'TB': 1024 ** 4 };
              const [size, unit] = sizeStr.split(' ');
              return parseFloat(size) * units[unit];
            };
            const aBytes1 = getBytes1(a.vector_space);
            const bBytes1 = getBytes1(b.vector_space);
            return sortConfig.direction === 'ascending'
              ? aBytes1 - bBytes1
              : bBytes1 - aBytes1;
          case 'ru_cost.total':
            const keys = sortConfig.key.split('.');
            const aValue = keys.reduce((obj, key) => obj?.[key], a) || 0;
            const bValue = keys.reduce((obj, key) => obj?.[key], b) || 0;
            return sortConfig.direction === 'ascending'
              ? aValue - bValue
              : bValue - aValue;
          default:
            // Your existing sorting logic
            return sortConfig.direction === 'ascending'
              ? (a[sortConfig.key] < b[sortConfig.key] ? -1 : 1)
              : (a[sortConfig.key] < b[sortConfig.key] ? 1 : -1);
        }
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  const filteredUsers = sortedUsers.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) 
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className="container-fluid mb-5 px-3">
        <div className="row g-5 pt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header pt-3 bg-white">

                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="text-secondary">User Management
                    </h4>
                  </div>

                </div>
              </div>

              <div className="table-responsive position-relative">
                <Row className="justify-content-center flex-grow-1">
                  <Col>
                    {isLoading ? (
                      <div className="text-center mt-5">
                        <Spinner
                          animation="border"
                          variant="primary"
                          role="status"
                          style={{ width: "3rem", height: "3rem" }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-2">Loading user data...</p>
                      </div>
                    ) : (
                      <Row>
                        <Col>
                          <UserList
                            users={currentUsers}
                            onEditUser={handleEditUser}
                            onDeleteUser={handleDeleteShow}
                            onSearch={handleSearch}
                            onSort={handleSort}
                            sortConfig={sortConfig}
                            currentPage={currentPage}
                            usersPerPage={usersPerPage}
                            totalUsers={filteredUsers.length}
                            paginate={paginate}

                          />
                        </Col>

                      </Row>
                    )}
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          <div>
            {alert && (
              <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
                {alert.message}
              </Alert>
            )}
          </div>
        </div >
      </div >

      
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton> 
            <Modal.Title>Edit User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedUser && (
              <UserEdit
                user={selectedUser}
                onUpdateUser={handleUpdateUser}
                onCancel={() => setShowModal(false)}
                currentUserRole={currentUser.role}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            {alert && (
              <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
                {alert.message}
              </Alert>
            )}
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={handleDeleteClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete the user "{selectedUser?.username}"?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteClose}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteUser}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

     
    </>
  );
}

export default AdminDashboard;